<script setup lang="ts">
import { ref, computed } from 'vue'
import { useUiStore } from '../stores/uiStore'
import LogoutDialog from './LogoutDialog.vue'
import UserInfoDialog from './UserInfoDialog.vue'
import ForgotPasswordDialog from './ForgotPasswordDialog.vue'
import AdminConsoleDialog from './AdminConsoleDialog.vue'

const uiStore = useUiStore()

const avatarSize = computed(() => Math.max(32, uiStore.fontSize + 8))
const avatarFontSize = computed(() => Math.max(12, uiStore.fontSize - 2))

interface User {
  id: number
  email: string
  username: string
  nickname: string
  role: 'sysadmin' | 'group_admin' | 'user'
}

interface Props {
  isLoggedIn: boolean
  user: User | null
  snapshotCount: number
  bookmarkCount: number
  noteCount: number
}

interface Emits {
  (e: 'show-auth-dialog'): void
  (e: 'logout-confirm'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const showMenu = ref(false)
const showUserInfo = ref(false)
const showLogoutDialog = ref(false)
const showForgotPasswordDialog = ref(false)
const showAdminConsole = ref(false)

const handleAvatarClick = () => {
  showMenu.value = !showMenu.value
}

const handleAdminConsole = () => {
  showAdminConsole.value = true
  showMenu.value = false
}

const handlePersonalInfo = () => {
  showUserInfo.value = true
  showMenu.value = false
}

const handleLoginLogout = () => {
  if (props.isLoggedIn) {
    showLogoutDialog.value = true
  } else {
    emit('show-auth-dialog')
  }
  showMenu.value = false
}

const handleLogoutConfirm = () => {
  showLogoutDialog.value = false
  emit('logout-confirm')
}
</script>

<template>
  <div class="avatar-wrapper">
    <!-- 頭像按鈕 -->
    <button class="avatar-button" @click="handleAvatarClick" :title="isLoggedIn ? user?.nickname : '未登入'">
      <span class="avatar-text">
        {{ isLoggedIn && user ? user.nickname[0].toUpperCase() : 'U' }}
      </span>
    </button>

    <!-- 下拉菜單 -->
    <div v-if="showMenu" class="dropdown-menu" @click.outside="showMenu = false">
      <button
        v-if="isLoggedIn && user?.role === 'sysadmin'"
        class="menu-item"
        @click="handleAdminConsole"
      >
        管理控制台
      </button>
      <button
        class="menu-item"
        :disabled="!isLoggedIn"
        :class="{ disabled: !isLoggedIn }"
        @click="handlePersonalInfo"
      >
        個人資訊
      </button>
      <div class="menu-divider"></div>
      <button class="menu-item" @click="handleLoginLogout">
        {{ isLoggedIn ? '登出' : '登入' }}
      </button>
    </div>

    <!-- 個人資訊對話框 -->
    <UserInfoDialog
      :is-open="showUserInfo"
      :user="user"
      :snapshot-count="snapshotCount"
      :bookmark-count="bookmarkCount"
      :note-count="noteCount"
      @close="showUserInfo = false"
      @update="() => {}"
    />

    <!-- 登出確認對話框 -->
    <LogoutDialog
      :is-open="showLogoutDialog"
      @confirm="handleLogoutConfirm"
      @cancel="showLogoutDialog = false"
    />

    <!-- 忘記密碼對話框 -->
    <ForgotPasswordDialog
      :is-open="showForgotPasswordDialog"
      @close="showForgotPasswordDialog = false"
    />

    <!-- 管理控制台對話框 -->
    <AdminConsoleDialog
      :is-open="showAdminConsole"
      @close="showAdminConsole = false"
    />
  </div>
</template>

<style scoped>
.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.avatar-button {
  min-width: v-bind(avatarSize + 'px');
  width: v-bind(avatarSize + 'px');
  height: v-bind(avatarSize + 'px');
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: v-bind(avatarFontSize + 'px');
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.avatar-button:hover {
  background: #2563eb;
  transform: scale(1.05);
}

.avatar-button:active {
  transform: scale(0.95);
}

.avatar-text {
  display: inline-block;
  line-height: 1;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}

.menu-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  color: #374151;
  font-size: 14px;
  transition: background 0.2s;
}

.menu-item:hover:not(.disabled) {
  background: #f3f4f6;
}

.menu-item.disabled {
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0;
}
</style>
